import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { format } from "date-fns";

import { FaColumns, FaExpand, FaCompress, FaLayerGroup } from "react-icons/fa";
import { MdOutlineSegment, MdOutlineFileDownload } from "react-icons/md";
import { IoIosArrowDown, IoMdClose } from "react-icons/io";

import {
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import TablePagination from "@mui/material/TablePagination";

import { DateRangeSelector } from "./../styles/CustomDateRangePicker";
import ModifyColumns from "./Tools/ModifyColumns";

import {
  INITIAL_COLUMNS,
  CAMPAIGN_COLUMNS,
  INITIAL_VIEW_BY_COLUMNS,
  COLUMNS_AD_SEGMENT,
  AD_DEVICE_COLUMNS,
  FILTER_OPTIONS,
  COLUMNS_GROUP_SEGMENT,
  AD_TOPTOTHER_COLUMNS,
  AD_CLICK_COLUMNS,
  AD_NETWORK_COLUMNS,
  AD_DEVICE_PLATFORM_COLUMNS,
} from "./config/ColumnsCampaign";

import {
  FilterComponent,
  apiRequestBodyBuilder,
  getFilteredData,
  formatSegmentData,
  getPriorityColumnsFromStorage,
} from "./config/tableHelper";
import { RenderTable } from "./config/tableWrapper";
import { generatePDF, generateCSV } from "./Components/DownloadFuncs";

// Skeleton styled identically to PaidAdsPage TableSkeleton
function TableSkeleton() {
  return (
    <div className="w-full rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      {/* Header Bar */}
      <div className="flex items-center justify-between pb-4 border-b border-gray-100 animate-pulse">
        <div className="h-4 w-32 bg-gray-200 rounded" />
        <div className="flex items-center gap-8 sm:gap-12">
          <div className="h-4 w-16 bg-gray-200 rounded hidden sm:block" />
          <div className="h-4 w-16 bg-gray-200 rounded" />
          <div className="h-4 w-20 bg-gray-200 rounded" />
          <div className="h-4 w-24 bg-gray-200 rounded hidden md:block" />
        </div>
      </div>

      {/* Rows */}
      <div className="divide-y divide-gray-100 animate-pulse">
        {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
          <div key={item} className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <div
                className="h-4 bg-gray-200 rounded"
                style={{ width: `${140 + (item % 3) * 35}px` }}
              />
            </div>
            <div className="flex items-center gap-8 sm:gap-12">
              <div className="h-4 w-14 bg-gray-100 rounded hidden sm:block" />
              <div className="h-4 w-16 bg-gray-100 rounded" />
              <div className="h-4 w-16 bg-gray-100 rounded" />
              <div className="h-4 w-20 bg-gray-100 rounded hidden md:block" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const CampaignsTable = () => {
  const [currentPage, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tableVisible, setTableVisible] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState(INITIAL_COLUMNS);

  const [isGroupDropdownVisible, setGroupDropdownVisible] = useState(false);
  const [showColumnsMenu, setShowColumnsMenu] = useState(false);
  const [showDatewiseType, setShowDatewiseType] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [isGroupListVisible, setIsGroupListVisible] = useState(false);
  const [showViewBy, setShowViewBy] = useState(false);
  const [showFilterBy, setShowFilterBy] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showTotal, setTotalShow] = useState(true);

  const [savedGroups, setSavedGroups] = useState([]);
  const [selectedGroupIds, SetSelectedGroupIds] = useState([]);
  const [currentGroupType, setCurrentGroupType] = useState(null);

  const [filterArray, setFilterArray] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState({ CampaignType: [] });

  const [segmentType, setSegmentType] = useState("default");
  const [viewMode, setViewMode] = useState("groups");
  const [random, setRandom] = useState(Math.random());
  const [total, setTotal] = useState({});
  const [currentOrderType, setCurrentOrderType] = useState(true);
  const [currentOrderVariable, setCurrentOrderVariable] = useState(null);

  const [isDownloading, setIsDownloading] = useState(false);

  const datePickerRef = useRef(null);
  const segmentButtonRef = useRef(null);
  const filterRef = useRef(null);
  const columnRef = useRef(null);
  const groupRef = useRef(null);
  const downloadRef = useRef(null);

  let d = new Date();
  let year = d.getFullYear();
  let month = d.getMonth();

  const [state, setState] = useState([
    {
      startDate: new Date(year, month, 1),
      endDate: new Date(),
      key: "selection",
    },
  ]);

  const filteredData = useMemo(() => {
    return getFilteredData(data, filterArray);
  }, [data, filterArray]);

  const currentData = filteredData.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage,
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickInsideMuiMenu = event.target.closest(
        ".groupwise-select-menu",
      );
      if (
        groupRef.current &&
        !groupRef.current.contains(event.target) &&
        !isClickInsideMuiMenu
      ) {
        setGroupDropdownVisible(false);
      }
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target) &&
        !isClickInsideMuiMenu
      ) {
        setShowFilterDropdown(false);
      }
      if (columnRef.current && !columnRef.current.contains(event.target)) {
        setShowColumnsMenu(false);
      }
      if (
        segmentButtonRef.current &&
        !segmentButtonRef.current.contains(event.target) &&
        !event.target.closest(".groupwise-select-menu")
      ) {
        setIsGroupListVisible(false);
      }
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setShowDatePicker(false);
      }
      if (downloadRef.current && !downloadRef.current.contains(event.target)) {
        setShowDownloadMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // FILE DOWNLOAD INTEGRATION
  const handleDownload = async (type) => {
    setShowDownloadMenu(false);
    setIsDownloading(true);
    try {
      const filename = `Campaigns_Report_${format(new Date(), "yyyy-MM-dd")}.${type}`;
      const dateRangeStr =
        state[0].startDate === state[0].endDate
          ? "All time"
          : `${format(state[0].startDate, "MMM dd, yyyy")} - ${format(state[0].endDate, "MMM dd, yyyy")}`;

      // Delay slightly so UI spinner renders
      await new Promise((resolve) => setTimeout(resolve, 100));

      if (type === "pdf") {
        await generatePDF(
          filteredData,
          columns,
          dateRangeStr,
          filename,
          "Campaign report",
        );
      } else if (type === "csv") {
        await generateCSV(
          filteredData,
          columns,
          dateRangeStr,
          filename,
          "Campaign report",
        );
      }
    } catch (error) {
      console.error(`Failed to generate ${type.toUpperCase()}:`, error);
    } finally {
      setIsDownloading(false);
    }
  };

  const toggleSegmentType = (type) => {
    toggleDropDownTabs("Hide all");
    setSegmentType(type);
    setRandom(Math.random());
    setGroupDropdownVisible(false);
  };

  useEffect(() => {
    setIsLoading(true);

    setPage(0);
    const fetchDataAndSetLoading = async () => {
      try {
        setData([]);
        setIsLoading(true);

        switch (segmentType) {
          case "default":
            if (viewMode === "groups") {
              await CampaignFilterButton();
            } else {
              await fetch_campaigns();
            }
            break;
          case "segment_date":
          case "date":
          case "week":
          case "month":
            if (viewMode === "groups") {
              await fetchSegmentGroupData();
            } else {
              await fetchSegmentData();
            }
            break;
          case "segment_device":
            await fetchSegmentDeviceData();
            break;
          case "segment_device_platform":
            await fetchSegmentDevicePlatformData();
            break;
          case "segment_network":
            await fetchSegmentNetworkData();
            break;
          case "segment_clicktype":
            await fetchSegmentClickTypeData();
            break;
          case "segment_topvsother":
            await fetchSegmentTopOtherData();
            break;
          case "compare":
            await fetchCompareCampaignsData();
            break;
          case "campaignfilter":
            await applyFilters();
            break;
          default:
            break;
        }
        await getCustomColumns();
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchDataAndSetLoading();
  }, [random, savedGroups, segmentType, viewMode]);

  useEffect(() => {
    fetchGroups();
  }, [currentGroupType]);

  useEffect(() => {
    fetchCurrentGroupType();
  }, []);

  // --- Helpers ---
  const API_BASE_URL = "https://api.confidanto.com";
  const updateSegmentColumns = (columns, type) => {
    let newCols = columns.map((item) => {
      if (["date", "week", "month", "segment"].includes(item.key)) {
        return {
          ...item,
          title: type.charAt(0).toUpperCase() + type.slice(1).toLowerCase(),
        };
      }
      return item;
    });

    if (type === "date") {
      newCols = newCols.map((item) =>
        item.key === "day_name" ? { ...item, visible: true } : item,
      );
    }
    return newCols;
  };

  const updateGroupColumnTitle = (columns, type) => {
    if (!type) return columns;
    return columns.map((col) => {
      if (col.key === "group_name") {
        return {
          ...col,
          title: `${type.charAt(0).toUpperCase() + type.slice(1)} Group`,
        };
      }
      return col;
    });
  };

  // --- Fetch Functions ---

  const fetchSegmentData = async () => {
    try {
      const requestBody = apiRequestBodyBuilder(state, {
        group_by: segmentType,
      });

      const res = await axios.post(
        `${API_BASE_URL}/campaigns/segment-data`,
        requestBody,
      );

      if (res.data) {
        setData(formatSegmentData(res.data, segmentType));
        const newCols = updateSegmentColumns(COLUMNS_AD_SEGMENT, segmentType);
        setColumns(newCols);
      }
    } catch (err) {
      console.error("Fetch failed:", err);
    }
  };

  const fetchSegmentNetworkData = async () => {
    try {
      const requestBody = apiRequestBodyBuilder(state);
      const res = await axios.post(
        `${API_BASE_URL}/campaigns/network`,
        requestBody,
      );
      if (Array.isArray(res.data)) {
        setColumns(AD_NETWORK_COLUMNS);
        setData(res.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchSegmentClickTypeData = async () => {
    try {
      const requestBody = apiRequestBodyBuilder(state);
      const res = await axios.post(
        `${API_BASE_URL}/campaigns/click-type`,
        requestBody,
      );
      if (Array.isArray(res.data)) {
        setColumns(AD_CLICK_COLUMNS);
        setData(res.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchSegmentTopOtherData = async () => {
    try {
      const requestBody = apiRequestBodyBuilder(state);
      const res = await axios.post(
        `${API_BASE_URL}/campaigns/top-vs-other`,
        requestBody,
      );
      if (Array.isArray(res.data)) {
        setColumns(AD_TOPTOTHER_COLUMNS);
        setData(res.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchSegmentDeviceData = async () => {
    try {
      const requestBody = apiRequestBodyBuilder(state);
      const res = await axios.post(
        `${API_BASE_URL}/campaigns/device`,
        requestBody,
      );
      if (Array.isArray(res.data)) {
        setColumns(AD_DEVICE_COLUMNS);
        setData(res.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchSegmentDevicePlatformData = async () => {
    try {
      const requestBody = apiRequestBodyBuilder(state);
      const res = await axios.post(
        `${API_BASE_URL}/device/mobile-device-platform-segment`,
        requestBody,
      );
      if (Array.isArray(res.data)) {
        setColumns(AD_DEVICE_PLATFORM_COLUMNS);
        setData(res.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetch_campaigns = async () => {
    setIsLoading(true);
    try {
      const requestBody = apiRequestBodyBuilder(state);
      const response = await axios.post(
        `${API_BASE_URL}/campaigns/datewise-data`,
        requestBody,
      );
      setData(response.data);
      setColumns(CAMPAIGN_COLUMNS);
      setTotal({});
      setTotalShow(true);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSegmentGroupData = async () => {
    const tempGroupsIds =
      selectedGroupIds.length > 0
        ? selectedGroupIds
        : savedGroups.map((item) => item.group_id);

    try {
      const requestBody = apiRequestBodyBuilder(state, {
        group_ids: tempGroupsIds,
        group_by: segmentType,
      });

      const res = await axios.post(
        `${API_BASE_URL}/campaign-group/segment-data`,
        requestBody,
      );

      if (res.data) {
        setData(formatSegmentData(res.data, segmentType));

        let newCols = updateSegmentColumns(COLUMNS_GROUP_SEGMENT, segmentType);
        newCols = updateGroupColumnTitle(newCols, currentGroupType);

        setColumns(newCols);
      }
    } catch (err) {
      console.error("Fetch failed:", err);
    }
  };

  const getCustomColumns = async () => {
    try {
      const requestBody = apiRequestBodyBuilder(null);

      const response = await axios.post(
        "https://api.confidanto.com/custom_columns/get-custom-columns",
        requestBody,
      );

      const temp = response.data.data.map((i) => ({
        id: i.id,
        title: i.custom_column,
        key: i.custom_column,
        visible: false,
        category: "Custom Columns",
        type: "number",
      }));

      setColumns((prevColumns) => {
        const prevColsFiltered = prevColumns.filter(
          (item) => item.category !== "Custom Columns",
        );
        const allColumns = [...prevColsFiltered, ...temp];

        const sorted = getPriorityColumnsFromStorage(allColumns);

        return sorted;
      });
    } catch (error) {
      setColumns((prevColumns) => {
        const sorted = getPriorityColumnsFromStorage(prevColumns);
        return sorted;
      });
      console.error("Error fetching custom columns:", error);
    }
  };

  const fetchCurrentGroupType = () => {
    let RequestBody = apiRequestBodyBuilder();
    axios
      .post(
        "https://api.confidanto.com/campaign-group2/group-info",
        RequestBody,
      )
      .then((res) => {
        setCurrentGroupType(res.data.type);
      })
      .catch((err) => {
        console.error("ERROR FETCHING GROUP TYPE");
      });
  };

  const fetchGroups = async () => {
    setIsLoading(true);

    try {
      let requestBody = apiRequestBodyBuilder(state, {
        type: currentGroupType === "keyword" ? "keywords" : currentGroupType,
      });

      const response = await axios.post(
        "https://api.confidanto.com/campaign-group/fetch-groups",
        requestBody,
      );
      if (response.data.groups.length > 0) {
        const groupsData = response.data.groups;
        setSavedGroups(groupsData);
        SetSelectedGroupIds(
          groupsData.map((group) => group.group_id.toString()),
        );
      } else {
        setViewMode("campaigns");
      }
    } catch (error) {
      setViewMode("campaigns");
    }
  };

  const applyFilters = async () => {
    setShowFilterDropdown(false);
    setShowFilterBy(false);
    setIsGroupListVisible(false);
    try {
      let requestBody = apiRequestBodyBuilder(null, {
        campaign_type: selectedFilters.CampaignType,
      });

      const res = await axios.post(
        "https://api.confidanto.com/campaigns/campaigns-by-campaign-types",
        requestBody,
      );
      setData(res.data);
      setColumns(CAMPAIGN_COLUMNS);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (segmentType === "groups" && selectedGroupIds.length > 0)
      CampaignFilterButton();
  }, [selectedGroupIds, segmentType]);

  const fetchCompareCampaignsData = async () => {
    try {
      let requestBody = apiRequestBodyBuilder(state);
      const res = await axios.post(
        "https://api.confidanto.com/campaigns/datewise-data",
        requestBody,
      );
      setData(res.data);
      setColumns(CAMPAIGN_COLUMNS);
    } catch (err) {
      console.error(err);
    }
    setShowDatePicker(false);
  };

  const handleGroupChange = (event) => SetSelectedGroupIds(event.target.value);

  const handleCampaignTypeChange = (event) => {
    const value = event.target.value;
    setSelectedFilters((prev) => ({
      ...prev,
      CampaignType:
        typeof value === "string" ? value.split(",").filter(Boolean) : value,
    }));
  };

  const getSelectedCampaignTypeNames = (selectedValues) =>
    FILTER_OPTIONS.filter((o) => selectedValues.includes(o.value))
      .map((o) => o.name)
      .join(", ");

  const getSelectedGroupNames = (selectedIds) =>
    savedGroups
      .filter((g) => selectedIds.includes(g.group_id))
      .map((g) => g.group_name)
      .join(", ");

  const CampaignFilterButton = async () => {
    let requestBody = apiRequestBodyBuilder(state, {
      group_ids: selectedGroupIds,
    });

    try {
      const response = await axios.post(
        "https://api.confidanto.com/campaign-group/datewise-data",
        requestBody,
      );
      const summarizedMetrics = response.data;
      setData(summarizedMetrics);

      let newCols = INITIAL_COLUMNS.map((col) => {
        if (col.key === "group_name") {
          if (currentGroupType !== null) {
            col.title = `${
              currentGroupType?.charAt(0).toUpperCase() +
              currentGroupType?.slice(1)
            } Group`;
          }
        }
        return col;
      });
      setColumns(newCols);
    } catch (error) {
      console.error(error);
    }
  };

  const toggleFullScreen = () => setIsFullScreen(!isFullScreen);

  const toggleDropDownTabs = (type) => {
    setShowDatePicker(type === "Calendar" ? !showDatePicker : false);
    setIsGroupListVisible(type === "Segment" ? !isGroupListVisible : false);
    setShowFilterDropdown(type === "Filter" ? !showFilterDropdown : false);
    setGroupDropdownVisible(
      type === "Category" ? !isGroupDropdownVisible : false,
    );
    setShowColumnsMenu(type === "Columns" ? !showColumnsMenu : false);
    setShowDownloadMenu(type === "Download" ? !showDownloadMenu : false);
  };

  const removeFilterFromArray = (id) => {
    setFilterArray((prev) => prev.filter((fil) => fil.id !== id));
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <div>
      <div
        className={`flex h-screen bg-white ${
          isFullScreen
            ? "fixed top-0 left-0 w-full h-full z-50 overflow-x-scroll"
            : ""
        }`}
      >
        <main className="flex flex-col flex-1 w-0 overflow-hidden p-6">
          <div
            className={`transition-opacity duration-300 ${isLoading ? "pointer-events-none opacity-50" : ""}`}
          >
            <DateRangeSelector
              state={state}
              setState={setState}
              setRandom={setRandom}
              showDatePicker={showDatePicker}
              toggleDropDownTabs={toggleDropDownTabs}
              datePickerRef={datePickerRef}
              Page="Campaigns"
            />
          </div>
          <div className="flex justify-between items-center mb-4">
            <div
              className={`relative transition-opacity duration-300 ${isLoading ? "pointer-events-none opacity-50" : ""}`}
              ref={filterRef}
            >
              <FilterComponent
                columns={columns}
                data={data}
                filterArray={filterArray}
                setFilterArray={setFilterArray}
                segmentType={segmentType}
              />
            </div>
            <div className="flex space-x-2">
              <div className="relative" ref={segmentButtonRef}>
                <button
                  disabled={isLoading}
                  className={`px-4 py-2 min-w-[80px] rounded flex flex-col justify-center items-center gap-1 transition-all ${
                    segmentType !== "default"
                      ? "bg-blue-100 text-blue-600"
                      : "bg-transparent text-gray-600"
                  } ${isLoading ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-100"}`}
                  onClick={() => toggleDropDownTabs("Segment")}
                >
                  <MdOutlineSegment className="text-md" />
                  <span className="text-sm">Segment</span>
                </button>
                {isGroupListVisible && (
                  <div className="absolute z-40 w-60 bg-white shadow-lg rounded-lg mt-2 p-4 border">
                    <div
                      className="p-2 hover:bg-gray-100 capitalize cursor-pointer"
                      onClick={() => {
                        toggleSegmentType("default");
                        setViewMode("campaigns");
                      }}
                    >
                      Reset
                    </div>
                    <p className="p-2 text-sm text-gray-400">By</p>
                    <button
                      className="p-2 flex items-center hover:bg-gray-50 cursor-pointer w-full justify-between"
                      onClick={() => setShowViewBy(!showViewBy)}
                    >
                      View By{" "}
                      <IoIosArrowDown
                        className={`ml-2 transform transition-transform duration-300 ${
                          showViewBy ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {showViewBy && (
                      <ul className="mx-2">
                        <li>
                          <div className="relative w-full cursor-pointer">
                            <div
                              className="w-full text-left hover:bg-slate-50 h-10 p-2 rounded-sm flex justify-between items-center"
                              onClick={() =>
                                setShowDatewiseType(!showDatewiseType)
                              }
                            >
                              <h2>Date</h2>
                              <IoIosArrowDown
                                className={`transition-transform ${
                                  showDatewiseType ? "rotate-180" : ""
                                }`}
                              />
                            </div>
                            {showDatewiseType && (
                              <div className="pl-3 mt-0.5 flex flex-col space-y-1">
                                <li
                                  className={`p-2  capitalize cursor-pointer ${
                                    segmentType === "date"
                                      ? "bg-blue-100 text-blue-600 "
                                      : "hover:bg-gray-100"
                                  }`}
                                  onClick={() => toggleSegmentType("date")}
                                >
                                  Day
                                </li>
                                <li
                                  className={`p-2  capitalize cursor-pointer ${
                                    segmentType === "week"
                                      ? "bg-blue-100 text-blue-600 "
                                      : "hover:bg-gray-100"
                                  }`}
                                  onClick={() => toggleSegmentType("week")}
                                >
                                  Week
                                </li>
                                <li
                                  className={`p-2  capitalize cursor-pointer ${
                                    segmentType === "month"
                                      ? "bg-blue-100 text-blue-600 "
                                      : "hover:bg-gray-100"
                                  }`}
                                  onClick={() => toggleSegmentType("month")}
                                >
                                  Month
                                </li>
                              </div>
                            )}
                          </div>
                        </li>

                        <li
                          className={`p-2  capitalize cursor-pointer ${
                            segmentType === "segment_device"
                              ? "bg-blue-100 text-blue-600 "
                              : "hover:bg-gray-100"
                          }`}
                          onClick={() => toggleSegmentType("segment_device")}
                        >
                          Device
                        </li>
                        <li
                          className={`p-2  capitalize cursor-pointer ${
                            segmentType === "segment_device_platform"
                              ? "bg-blue-100 text-blue-600 "
                              : "hover:bg-gray-100"
                          }`}
                          onClick={() =>
                            toggleSegmentType("segment_device_platform")
                          }
                        >
                          Device Platform
                        </li>
                        <li
                          className={`p-2  capitalize cursor-pointer ${
                            segmentType === "segment_network"
                              ? "bg-blue-100 text-blue-600 "
                              : "hover:bg-gray-100"
                          }`}
                          onClick={() => toggleSegmentType("segment_network")}
                        >
                          Network
                        </li>
                        <li
                          className={`p-2  capitalize cursor-pointer ${
                            segmentType === "segment_clicktype"
                              ? "bg-blue-100 text-blue-600 "
                              : "hover:bg-gray-100"
                          }`}
                          onClick={() => toggleSegmentType("segment_clicktype")}
                        >
                          Click Type
                        </li>
                        <li
                          className={`p-2  capitalize cursor-pointer ${
                            segmentType === "segment_topvsother"
                              ? "bg-blue-100 text-blue-600 "
                              : "hover:bg-gray-100"
                          }`}
                          onClick={() =>
                            toggleSegmentType("segment_topvsother")
                          }
                        >
                          Top vs Other
                        </li>
                      </ul>
                    )}
                    <button
                      className={`p-2 flex items-center cursor-pointer justify-between w-full ${
                        segmentType === "campaignfilter"
                          ? "bg-blue-100 text-blue-600"
                          : " hover:bg-gray-50"
                      }`}
                      onClick={() => setShowFilterBy(!showFilterBy)}
                    >
                      Campaign Types{" "}
                      <IoIosArrowDown
                        className={`ml-2 transform transition-transform duration-300 ${
                          showFilterBy ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    {showFilterBy && (
                      <div className="space-y-2 pt-1">
                        <FormControl fullWidth>
                          <InputLabel id="campaign-type-filter-label">
                            Campaign types
                          </InputLabel>
                          <Select
                            labelId="campaign-type-filter-label"
                            multiple
                            value={selectedFilters.CampaignType ?? []}
                            onChange={handleCampaignTypeChange}
                            input={<OutlinedInput label="Campaign types" />}
                            renderValue={getSelectedCampaignTypeNames}
                            MenuProps={{
                              className: "groupwise-select-menu",
                            }}
                          >
                            {FILTER_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                <Checkbox
                                  checked={selectedFilters.CampaignType?.includes(
                                    opt.value,
                                  )}
                                />
                                <ListItemText primary={opt.name} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Button
                          variant="contained"
                          color="primary"
                          disabled={!selectedFilters.CampaignType?.length}
                          onClick={() => {
                            toggleSegmentType("campaignfilter");
                          }}
                          className="mt-2"
                        >
                          Apply
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="relative" ref={groupRef}>
                <button
                  disabled={isLoading}
                  className={`px-4 py-2 min-w-[80px] rounded flex flex-col justify-center items-center gap-1 transition-all ${
                    isLoading
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-slate-100"
                  } bg-transparent text-gray-600`}
                  onClick={() => {
                    if (viewMode === "groups") {
                      setViewMode("campaigns");
                    } else {
                      toggleDropDownTabs("Category");
                    }
                  }}
                >
                  <FaLayerGroup className="text-md" />
                  <span className="text-sm">
                    {viewMode === "groups" ? "Campaign" : "Groups"}
                  </span>
                </button>
                {isGroupDropdownVisible && (
                  <div className="absolute right-0 bg-white shadow-md rounded p-4 mt-2 z-40 border w-64 space-y-2">
                    {savedGroups && savedGroups.length > 0 ? (
                      <>
                        <FormControl fullWidth>
                          <InputLabel className="capitalize">
                            {currentGroupType === null
                              ? ""
                              : currentGroupType + " "}
                            Groups
                          </InputLabel>{" "}
                          <Select
                            multiple
                            value={selectedGroupIds}
                            onChange={handleGroupChange}
                            input={
                              <OutlinedInput
                                label={`${currentGroupType} Groups`}
                              />
                            }
                            renderValue={getSelectedGroupNames}
                            MenuProps={{ className: "groupwise-select-menu" }}
                          >
                            {savedGroups.map((data) => (
                              <MenuItem
                                key={data.group_id}
                                value={data.group_id}
                              >
                                <Checkbox
                                  checked={selectedGroupIds.includes(
                                    data.group_id,
                                  )}
                                />
                                <ListItemText primary={data.group_name} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Button
                          variant="contained"
                          color="primary"
                          disabled={!selectedGroupIds?.length}
                          onClick={() => {
                            setViewMode("groups");
                            setSegmentType("default");
                            toggleDropDownTabs("Hide all");
                          }}
                          className="mt-2"
                        >
                          Apply
                        </Button>
                      </>
                    ) : (
                      <div className="flex flex-col items-center justify-center p-6 text-center">
                        <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
                          <FaLayerGroup className="text-gray-400 text-lg" />
                        </div>

                        <p className="text-sm font-medium text-gray-700">
                          No groups available
                        </p>

                        <p className="mt-1 text-xs text-gray-500 leading-relaxed">
                          You don’t have any groups created yet. Please create a
                          group to manage and apply group-based ads.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div
                className={`relative transition-opacity duration-300 ${isLoading ? "pointer-events-none opacity-50" : ""}`}
                ref={downloadRef}
              >
                {/* Updated Download Button with Multi-format */}
                <button
                  disabled={isLoading || isDownloading}
                  className={`px-4 py-2 min-w-[80px] rounded flex flex-col justify-center items-center gap-1 transition-all ${
                    isLoading || isDownloading
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-slate-100"
                  } ${showDownloadMenu ? "bg-blue-100 text-blue-600" : "bg-transparent text-gray-600"}`}
                  onClick={() => toggleDropDownTabs("Download")}
                >
                  {isDownloading ? (
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : (
                    <MdOutlineFileDownload className="text-md" />
                  )}
                  <span className="text-sm">
                    {isDownloading ? "Downloading" : "Download"}
                  </span>
                </button>
                {showDownloadMenu && (
                  <div className="absolute right-0 z-40 w-36 bg-white shadow-lg rounded-lg mt-2 p-2 border">
                    <div
                      className="p-2 hover:bg-gray-100 cursor-pointer rounded flex items-center justify-between text-gray-700"
                      onClick={() => handleDownload("pdf")}
                    >
                      .PDF
                    </div>
                    <div
                      className="p-2 hover:bg-gray-100 cursor-pointer rounded flex items-center justify-between text-gray-700"
                      onClick={() => handleDownload("csv")}
                    >
                      .CSV
                    </div>
                  </div>
                )}
              </div>

              <button
                disabled={isLoading}
                className={`px-4 py-2 min-w-[80px] rounded flex flex-col justify-center items-center gap-1 transition-all ${
                  isFullScreen
                    ? "bg-blue-100 text-blue-600"
                    : "bg-transparent text-gray-600"
                } ${isLoading ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-100"}`}
                onClick={toggleFullScreen}
              >
                {isFullScreen ? (
                  <FaCompress className="text-md" />
                ) : (
                  <FaExpand className="text-md" />
                )}
                <span className="text-sm">
                  {isFullScreen ? "Collapse" : "Expand"}
                </span>
              </button>
              <div className="relative" ref={columnRef}>
                <button
                  disabled={isLoading}
                  className={`px-4 py-2 min-w-[80px] rounded flex flex-col justify-center items-center gap-1 transition-all ${
                    isLoading
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-slate-100"
                  } bg-transparent text-gray-600`}
                  onClick={() => toggleDropDownTabs("Columns")}
                >
                  <FaColumns className="text-md" />
                  <span className="text-sm">Columns</span>
                </button>
                {showColumnsMenu && (
                  <ModifyColumns
                    columns={columns}
                    setColumns={setColumns}
                    setTableVisible={setTableVisible}
                    setShowColumnsMenu={setShowColumnsMenu}
                    setTotalShow={setTotalShow}
                    setTotal={setTotal}
                  />
                )}
              </div>
            </div>
          </div>
          {tableVisible && (
            <div className=" w-full mb-12 h-full">
              {filterArray.length > 0 && (
                <div className="w-full flex items-center p-2 relative">
                  <div className="flex flex-row w-11/12 overflow-auto scroll-container">
                    {filterArray.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between p-2 border rounded-lg min-w-fit mx-1"
                      >
                        <h2>
                          {item.title} {item.sign}{" "}
                          {Array.isArray(item.value)
                            ? item.value.join(", ")
                            : item.value}
                        </h2>
                        <button
                          onClick={() => removeFilterFromArray(item.id)}
                          className="ml-2"
                        >
                          <IoMdClose />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="absolute right-5 text-2xl hover:bg-red-500 hover:text-white">
                    <button onClick={() => setFilterArray([])}>
                      <IoMdClose />
                    </button>
                  </div>
                </div>
              )}
              {/* Table & Skeleton Loading */}
              {isLoading ? (
                <div className="mt-4">
                  <TableSkeleton />
                </div>
              ) : filteredData.length > 0 ? (
                <>
                  <div className="overflow-auto w-full max-h-[calc(100vh-250px)] scrollbar-thin scrollbar-track-gray-200 scrollbar-thumb-gray-400">
                    <RenderTable
                      columns={columns}
                      data={filteredData}
                      variant="performance"
                      enableExpandCollapse={state.length > 1}
                      currentPage={currentPage + 1}
                      rowsPerPage={rowsPerPage}
                    />
                  </div>
                  <TablePagination
                    component="div"
                    count={filteredData.length}
                    page={currentPage}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  />
                </>
              ) : (
                <div className="flex justify-center items-center h-60 mt-8">
                  <h2>No Data Found</h2>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};
export default CampaignsTable;
